import React, { useState } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Button,
} from '@mui/material';

const TreatmentForm = ({
  treatment,
  index,
  templateVersions,
  errors,
  isActive,
  onTreatmentChange,
  getSafeTemplateVersion,
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleDescriptionExpansion = () => {
    setIsDescriptionExpanded(prev => !prev);
  };

  const shouldShowMoreButton = description => {
    if (!description) return false;
    const wordCount = description.trim().split(/\s+/).length;
    return wordCount > 30;
  };

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid size={{ xs: 12, sm: 2 }}>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          label="Treatment Name"
          value={treatment.name}
          onChange={e => onTreatmentChange(index, 'name', e.target.value)}
          error={!!errors[`treatment_${index}_name`]}
          helperText={errors[`treatment_${index}_name`]}
          required
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 2 }}>
        <FormControl
          fullWidth
          size="small"
          margin="dense"
          sx={{
            '& .MuiInputLabel-root': { marginBottom: 0.5 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <InputLabel>Type</InputLabel>
          <Select
            value={treatment.treatment_type}
            onChange={e =>
              onTreatmentChange(index, 'treatment_type', e.target.value)
            }
            disabled={isActive}
            sx={{ mt: 0.5 }}
          >
            <MenuItem value="control">Control</MenuItem>
            <MenuItem value="treatment">Treatment</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12, sm: 2.5 }}>
        <FormControl
          fullWidth
          size="small"
          margin="dense"
          sx={{
            '& .MuiInputLabel-root': { marginBottom: 0.5 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <InputLabel>Template Version</InputLabel>
          <Select
            value={getSafeTemplateVersion(treatment.template_version)}
            onChange={e =>
              onTreatmentChange(
                index,
                'template_version',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            displayEmpty
            disabled={isActive}
            sx={{ mt: 0.5 }}
          >
            <MenuItem value="" disabled>
              Select a template version
            </MenuItem>
            {templateVersions.length === 0 ? (
              <MenuItem disabled>Loading versions...</MenuItem>
            ) : (
              templateVersions.map(version => (
                <MenuItem
                  key={version.version}
                  value={parseInt(version.version)}
                >
                  Version {version.version} -{' '}
                  {version.created_at
                    ? new Date(version.created_at).toLocaleDateString()
                    : 'Unknown date'}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12, sm: 5.5 }}>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          label="Hypothesis"
          value={treatment.hypothesis}
          onChange={e => onTreatmentChange(index, 'hypothesis', e.target.value)}
          placeholder="What do you expect this treatment to achieve?"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Description"
            value={treatment.description}
            onChange={e =>
              onTreatmentChange(index, 'description', e.target.value)
            }
            multiline
            rows={isDescriptionExpanded ? 20 : 4}
            sx={{
              '& .MuiInputBase-root': {
                paddingBottom: shouldShowMoreButton(treatment.description)
                  ? '32px'
                  : undefined,
              },
              '& .MuiInputBase-input': {
                overflow: 'hidden !important',
                resize: 'none',
              },
              '& textarea': {
                overflow: 'hidden !important',
                resize: 'none',
              },
            }}
          />
          {shouldShowMoreButton(treatment.description) && (
            <Button
              size="small"
              onClick={toggleDescriptionExpansion}
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                textTransform: 'none',
                minWidth: 'auto',
                padding: '4px 8px',
                fontSize: '0.75rem',
                zIndex: 1,
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {isDescriptionExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default TreatmentForm;
